<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between">
                <div>
                    <h1 class="text-h5 font-weight-bold">Driver Leaderboard</h1>
                    <p class="text-body-2 text-grey mt-1">Safest drivers by safety score</p>
                </div>
                <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="fetchData"/>
            </v-col>
        </v-row>

        <!-- Filters row -->
        <v-row class="mb-4" align="center">
            <!-- Period toggle -->
            <v-col cols="auto">
                <v-btn-toggle v-model="dateRange" mandatory density="compact" variant="outlined"
                              color="primary" @update:model-value="fetchData">
                    <v-btn :value="7">7d</v-btn>
                    <v-btn :value="30">30d</v-btn>
                    <v-btn :value="90">90d</v-btn>
                </v-btn-toggle>
            </v-col>
            <!-- Company filter (admin only) -->
            <v-col v-if="isAdmin" cols="12" sm="4" md="3">
                <company-select v-model="selectedCompany" :items="companies" label="Company" density="compact" variant="outlined"
                                hide-details clearable @update:model-value="fetchData"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

        <template v-if="!loading">
            <!-- Podium: top 3 -->
            <v-row v-if="podium.length" class="mb-4 justify-center align-end">
                <v-col v-for="d in podium" :key="d.id" cols="12" sm="4" md="3"
                       :order="d.podiumOrder" class="d-flex justify-center">
                    <v-card elevation="3" class="text-center pa-4" :class="d.rank === 1 ? 'mt-0' : 'mt-4'"
                            width="100%" @click="openDriver(d.id)" style="cursor: pointer;">
                        <v-icon :color="medalColor(d.rank)" size="32">mdi-medal</v-icon>
                        <div class="my-2 d-flex justify-center">
                            <UserAvatar :src="d.photo" :name="d.name" :size="d.rank === 1 ? 72 : 56"/>
                        </div>
                        <div class="font-weight-bold text-truncate">{{ d.name }}</div>
                        <div class="text-h5 font-weight-bold mt-1" :class="scoreTextColor(d.score)">
                            {{ formatScore(d.score) }}
                        </div>
                        <div class="text-caption text-grey mb-1">Rank #{{ d.rank }}</div>
                        <!-- Rank trend -->
                        <div class="d-flex justify-center align-center gap-1 mb-1">
                            <v-chip v-if="rankDelta(d)" size="x-small" :color="rankDelta(d) > 0 ? 'success' : 'error'"
                                    variant="tonal">
                                {{ rankDelta(d) > 0 ? '▲' : '▼' }} {{ Math.abs(rankDelta(d)) }}
                            </v-chip>
                            <v-chip v-if="isPersonalBest(d)" size="x-small" color="amber" variant="flat">
                                🏆 PB
                            </v-chip>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Standings table -->
            <v-row>
                <v-col cols="12">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Standings</h3>
                        <v-table density="compact" hover>
                            <thead>
                            <tr>
                                <th class="text-center" style="width:60px">Rank</th>
                                <th>Driver</th>
                                <th class="text-center" style="width:90px">Score</th>
                                <th class="text-center" style="width:60px">Trend</th>
                                <th style="min-width:120px">Progress</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="d in ranked" :key="d.id" style="cursor: pointer;"
                                @click="openDriver(d.id)">
                                <td class="text-center">{{ d.rank }}</td>
                                <td>
                                    <div class="d-flex align-center">
                                        <UserAvatar :src="d.photo" :name="d.name" size="28" class="mr-2"/>
                                        <span>{{ d.name }}</span>
                                        <v-chip v-if="isPersonalBest(d)" size="x-small" color="amber"
                                                variant="flat" class="ml-1">PB</v-chip>
                                    </div>
                                </td>
                                <td class="text-center">
                                    <v-chip size="small" variant="flat" :color="scoreColor(d.score)">
                                        {{ formatScore(d.score) }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <template v-if="rankDelta(d)">
                                        <span :class="rankDelta(d) > 0 ? 'text-success' : 'text-error'"
                                              class="text-caption font-weight-bold">
                                            {{ rankDelta(d) > 0 ? '▲' : '▼' }}{{ Math.abs(rankDelta(d)) }}
                                        </span>
                                    </template>
                                    <span v-else class="text-grey text-caption">—</span>
                                </td>
                                <td>
                                    <div v-if="d.score != null" class="d-flex align-center gap-1">
                                        <v-progress-linear
                                            :model-value="tierProgress(d.score).pct"
                                            :color="tierProgress(d.score).color"
                                            height="6"
                                            rounded
                                            class="flex-grow-1"
                                            style="min-width:80px"
                                        />
                                        <span class="text-caption text-grey text-no-wrap" style="font-size:10px">
                                            {{ tierProgress(d.score).label }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </v-table>
                        <div v-if="!rows.length" class="text-center text-grey py-6">
                            No scored drivers yet.
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {send} from '@/composables/apiClient'
import UserAvatar from '@/components/UserAvatar.vue'
import {useAuthStore} from '@/stores/authStore'
import {useRouter} from 'vue-router'
import CompanySelect from "@/components/globals/company-select.vue";

const router = useRouter()
const authStore = useAuthStore()
const {user} = authStore

const isAdmin = computed(() => user?.type === 'admin')
const loading = ref(false)
const rows = ref([])

const companies = ref([])
const selectedCompany = ref(user?.type !== 'admin' ? user?.company?.id : null)
const dateRange = ref(30)

// Persisted state keys
const PB_KEY = (id) => `sb_pb_${id}`
const RANKS_KEY = 'sb_prev_ranks'

// Previous rank map restored from sessionStorage — used for rank-trend arrows.
const prevRanks = ref(
    JSON.parse(sessionStorage.getItem(RANKS_KEY) || 'null') ?? {}
)

const ranked = computed(() => rows.value.map((d, i) => ({...d, rank: i + 1})))
const podium = computed(() =>
    ranked.value.slice(0, 3).map(d => ({...d, podiumOrder: {1: 2, 2: 1, 3: 3}[d.rank]}))
)

const medalColor = (rank) => ({1: 'amber', 2: 'blue-grey-lighten-1', 3: 'brown-lighten-1'}[rank] ?? 'grey')
const formatScore = (s) => (s == null ? '-' : Number(s).toFixed(1))
const scoreColor = (s) => {
    if (s == null) return 'grey'
    if (s >= 8.0) return 'success'
    if (s >= 5.0) return 'warning'
    return 'error'
}
const scoreTextColor = (s) => {
    if (s == null) return ''
    if (s >= 8.0) return 'text-green'
    if (s >= 5.0) return 'text-orange'
    return 'text-red'
}

// Rank delta: positive = moved up (rank number got smaller), negative = dropped.
const rankDelta = (d) => {
    const prev = prevRanks.value[d.id]
    if (prev == null) return null
    return prev - d.rank
}

// Personal best: compare current score against localStorage-persisted all-time high.
const isPersonalBest = (d) => {
    if (d.score == null) return false
    const stored = parseFloat(localStorage.getItem(PB_KEY(d.id)))
    return !isNaN(stored) && d.score >= stored
}

// Progress toward next score tier, as a percentage within the current band.
// Bands: 0-5 → Good (50%), 5-8 → Excellent (30%), 8-10 → at top (20%)
const tierProgress = (score) => {
    if (score == null) return {pct: 0, color: 'grey', label: ''}
    if (score >= 8.0) {
        const pct = Math.min(100, ((score - 8) / 2) * 100)
        return {pct, color: 'success', label: 'Excellent'}
    }
    if (score >= 5.0) {
        const pct = ((score - 5) / 3) * 100
        const remaining = (8 - score).toFixed(1)
        return {pct, color: 'warning', label: `${remaining} to Excellent`}
    }
    const pct = (score / 5) * 100
    const remaining = (5 - score).toFixed(1)
    return {pct, color: 'error', label: `${remaining} to Good`}
}

const openDriver = (id) => {
    if (!id) return
    router.push({name: 'driver', params: {driver: id}, query: {tab: 'analytics'}})
}

const fetchData = async () => {
    loading.value = true
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - dateRange.value)

    const params = {
        limit: 100,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
    }
    if (selectedCompany.value) params.company = selectedCompany.value

    try {
        const response = await send('get', 'drivers/scores/leaderboard', params)
        const data = response?.data?.data ?? []

        // Before updating ranked list, snapshot current ranks for next load.
        const currentRanks = {}
        ranked.value.forEach(d => { currentRanks[d.id] = d.rank })
        if (Object.keys(currentRanks).length) {
            prevRanks.value = currentRanks
            sessionStorage.setItem(RANKS_KEY, JSON.stringify(currentRanks))
        }

        rows.value = data

        // Update personal bests in localStorage.
        data.forEach(d => {
            if (d.score == null) return
            const key = PB_KEY(d.id)
            const stored = parseFloat(localStorage.getItem(key))
            if (isNaN(stored) || d.score > stored) {
                localStorage.setItem(key, d.score)
            }
        })
    } catch (e) {
        console.error('Failed to fetch leaderboard:', e)
        rows.value = []
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchData()
    if (isAdmin.value) {
        send('get', 'companies', {list: true}).then(r => {
            companies.value = r?.data?.data ?? []
        }).catch(e => console.warn('Could not load companies filter', e))
    }
})
</script>
